import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
} from '@chakra-ui/react';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type {
  ColDef,
  ICellRendererParams,
  RowDragCallbackParams,
} from 'ag-grid-community';

import type { LHCDetailInvoiceRowData } from '../../types';
import { shouldShowMutationDisplayOnRow } from '../../utils/lhc-mutation-group';
import {
  BankMutationNoCellRenderer,
  BillToCellRenderer,
  DifferenceAmountCellRenderer,
  PaymentProofCellRenderer,
  POCellRenderer,
} from './lhc-detail-cell-renderers';

export interface LHCDetailActionHandlers {
  onChangeMutation: (row: LHCDetailInvoiceRowData) => void;
  onAddMutation: (row: LHCDetailInvoiceRowData) => void;
  onEditRemark: (row: LHCDetailInvoiceRowData) => void;
  onMoveOrder: (row: LHCDetailInvoiceRowData) => void;
  onDelete: (row: LHCDetailInvoiceRowData) => void;
  onAddRelatedToLhc?: (row: LHCDetailInvoiceRowData) => void;
  onViewPaymentProof?: (row: LHCDetailInvoiceRowData) => void;
  onSearchMutation?: (row: LHCDetailInvoiceRowData) => void;
  onViewMutationHistory?: (row: LHCDetailInvoiceRowData) => void;
  onOpenMutationDetail?: (
    row: LHCDetailInvoiceRowData,
    bankMutationId: string
  ) => void;
}

type LHCDetailActionCellRendererParams = LHCDetailActionHandlers &
  ICellRendererParams<LHCDetailInvoiceRowData>;

function ActionCellRenderer(params: LHCDetailActionCellRendererParams) {
  const data = params.data;

  if (!data) {
    return '-';
  }

  if (data.isChildRow) {
    if (!data.canAddToLhc || !params.onAddRelatedToLhc) {
      return '-';
    }

    return (
      <Flex align="center" h="100%">
        <Button
          variant="link"
          p={0}
          minH="auto"
          h="auto"
          color="#0000A3"
          fontWeight={400}
          fontSize="14px"
          lineHeight="20px"
          _focusVisible={{ boxShadow: 'outline' }}
          onClick={(event) => {
            event.stopPropagation();
            params.onAddRelatedToLhc?.(data);
          }}
        >
          Tambah ke LHC
        </Button>
      </Flex>
    );
  }

  const showMutationActions = data.isMutationDisplayAnchor !== false;

  return (
    <Flex
      align="center"
      h="100%"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          variant="outline"
          borderColor="#0000a3"
          color="#0000a3"
          borderRadius="md"
          rightIcon={<KeyboardArrowDownIcon fontSize="small" />}
          _hover={{ bgColor: 'blue.50' }}
          _active={{ bgColor: 'blue.100' }}
        >
          Action
        </MenuButton>
        <Portal>
          <MenuList
            zIndex={2000}
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
            letterSpacing="0%"
          >
            {showMutationActions ? (
              <>
                <MenuItem onClick={() => params.onChangeMutation(data)}>
                  <Flex align="center" gap={2}>
                    <EditIcon style={{ fontSize: 18 }} />
                    Ganti Mutasi
                  </Flex>
                </MenuItem>
                <MenuItem onClick={() => params.onAddMutation(data)}>
                  <Flex align="center" gap={2}>
                    <AddIcon style={{ fontSize: 18 }} />
                    Tambah Mutasi
                  </Flex>
                </MenuItem>
              </>
            ) : null}
            <MenuItem onClick={() => params.onEditRemark(data)}>
              <Flex align="center" gap={2}>
                <EditIcon style={{ fontSize: 18 }} />
                Keterangan
              </Flex>
            </MenuItem>
            <MenuItem onClick={() => params.onMoveOrder(data)}>
              <Flex align="center" gap={2}>
                <DragIndicatorIcon style={{ fontSize: 18 }} />
                Pindah Urutan
              </Flex>
            </MenuItem>
            <MenuItem color="red" onClick={() => params.onDelete(data)}>
              <Flex align="center" gap={2}>
                <DeleteForeverIcon style={{ fontSize: 18 }} />
                Delete
              </Flex>
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  );
}

export function buildLHCDetailColumnDefs(
  actionHandlers: LHCDetailActionHandlers,
  options?: { isDragMode?: boolean }
): ColDef<LHCDetailInvoiceRowData>[] {
  return [
    {
      field: 'no',
      headerName: 'No.',
      width: 60,
      pinned: 'left',
      resizable: false,
      rowDrag: options?.isDragMode
        ? (params: RowDragCallbackParams<LHCDetailInvoiceRowData>) =>
            !!(params.data && !params.data.isChildRow)
        : false,
      valueFormatter: ({ value }) =>
        typeof value === 'number' ? String(value) : value || '',
    },
    {
      field: 'poNumber',
      headerName: 'PO',
      width: 220,
      pinned: 'left',
      cellRenderer: POCellRenderer,
      autoHeight: true,
    },
    { field: 'plant', headerName: 'Plant', width: 100 },
    { field: 'deliveryOrderNo', headerName: 'Surat Jalan/SO', width: 200 },
    { field: 'invoiceDate', headerName: 'Tgl. Faktur', width: 110 },
    { field: 'invoiceNo', headerName: 'No. Faktur', width: 140 },
    {
      field: 'billTo',
      headerName: 'Bill to',
      width: 100,
      cellRenderer: BillToCellRenderer,
      cellRendererParams: (
        params: ICellRendererParams<LHCDetailInvoiceRowData>
      ) => ({
        value: params.value,
        data: params.data,
        onViewMutationHistory: actionHandlers.onViewMutationHistory,
      }),
    },
    { field: 'customerName', headerName: 'Nama Customer', width: 180 },
    { field: 'outletName', headerName: 'Nama Outlet', width: 160 },
    {
      field: 'bankMutationNo',
      headerName: 'No. Mutasi',
      width: 180,
      autoHeight: true,
      cellRenderer: BankMutationNoCellRenderer,
      cellRendererParams: (
        params: ICellRendererParams<LHCDetailInvoiceRowData>
      ) => ({
        value: params.value,
        data: params.data,
        onSearchClick: actionHandlers.onSearchMutation,
        onOpenMutationDetail: actionHandlers.onOpenMutationDetail,
      }),
    },
    { field: 'poValue', headerName: 'Nilai PO (Rp)', width: 130 },
    { field: 'soValue', headerName: 'Nilai SO (Rp)', width: 130 },
    { field: 'invoiceValue', headerName: 'Nilai Faktur (Rp)', width: 150 },
    {
      field: 'differenceAmount',
      headerName: 'Selisih +/-',
      width: 130,
      cellRenderer: DifferenceAmountCellRenderer,
    },
    { field: 'mutationValue', headerName: 'Nilai Mutasi (Rp)', width: 150,
      valueFormatter: ({ data, value }) =>
        shouldShowMutationDisplayOnRow(data) ? value : '-',
    },
    { field: 'depositUsed', headerName: 'Pakai Deposit', width: 140 },
    {
      field: 'hasPaymentProof',
      headerName: 'Bukti Trf',
      width: 100,
      cellRenderer: PaymentProofCellRenderer,
      cellRendererParams: (
        params: ICellRendererParams<LHCDetailInvoiceRowData>
      ) => ({
        value: params.value,
        data: params.data,
        onViewClick: actionHandlers.onViewPaymentProof,
      }),
      sortable: false,
    },
    { field: 'bank', headerName: 'Bank', width: 120 },
    { field: 'remark', headerName: 'Keterangan', width: 150 },
    {
      colId: 'actions',
      headerName: 'Action',
      width: 160,
      cellRenderer: ActionCellRenderer,
      cellRendererParams: actionHandlers,
      sortable: false,
      filter: false,
      resizable: false,
    },
  ];
}
